"use client";

import {
  type FC,
  type Ref,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  usePubSub,
  useOnce,
  useDebouncedState,
  FormInputPopupTypeOptions,
  type FormInputProps,
  type DynamicInputValidationResult,
  type ConditionalFieldRules,
  type DataFieldValue,
  type Timeout,
  type UserPromptRecord,
  FormInput,
  useDebug,
} from "@huub-web/ui";
import { isArray } from "@huub-web/utils";

import { useTemplates } from "../../hooks/useTemplates";
import { useDisabledIf } from "../../hooks/useDisabledIf";
import { useVisibleIf } from "../../hooks/useVisibleIf";
import { useRegisterInputVisibility } from "../../hooks/useInputVisibility";
import { useTemplateParent } from "../../contexts/TemplateParentContext";
import type { InputFieldProps } from "../../types/InputFieldProps";

export interface FormInputTemplateProps extends InputFieldProps {
  /**
   * If the field is visible based on the provided rules.
   */
  visibleIf?: ConditionalFieldRules;
  /**
   * If the field is disabled based on the provided rules.
   */
  disabledIf?: ConditionalFieldRules;
  /**
   * The width of the field, in percentage.
   */
  width?: string;
  /**
   * @deprecated No longer used - kept for backwards compatibility
   */
  helpPrompt?: UserPromptRecord;
  /**
   * @deprecated Use InputVisibilityContext instead. This prop is no longer used.
   */
  onVisibilityChange?: (id: string, visible: boolean) => void;
}

/**
 * A template for rendering a FormInput component.
 */
export const FormInputTemplate: FC<
  FormInputTemplateProps & { ref?: Ref<HTMLDivElement> }
> = ({
  id,
  type,
  endpoint,
  linkedFields,
  disabled,
  visibleIf,
  disabledIf,
  width,
  error: errorProp,
  validation,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onVisibilityChange: _onVisibilityChange,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  helpPrompt: _helpPrompt,
  ref,
  ...props
}) => {
  const { updateDataFieldValue, registerDataFieldValue } = useTemplates();
  const pubSub = usePubSub();
  const { log } = useDebug();

  const { id: parentID, data: parentData } = useTemplateParent() ?? {};

  /**
   * Resolves the initial value for this field. Uses `props.value` when
   * explicitly provided; otherwise falls back to the parent data context
   * by stripping the parent ID prefix from this field's ID.
   */
  const resolvedValue = useMemo<DataFieldValue>(() => {
    if (props.value !== undefined) return props.value;
    if (!parentData || !parentID || !id) return undefined;
    const fieldName = id.startsWith(`${parentID}.`)
      ? id.slice(parentID.length + 1)
      : undefined;
    return fieldName ? parentData[fieldName] : undefined;
  }, [props.value, parentData, parentID, id]);

  const [error, setError] = useState<string | null>(errorProp ?? null);
  const [hasSuccess, setSuccess] = useState<boolean>(false);

  /**
   * Tracks whether the user has made a change that the backend
   * doesn't know about yet. Reset when validation passes;
   * restored if the POST fails.
   */
  const isDirtyRef = useRef(false);
  const validationRef = useRef<DynamicInputValidationResult | null>(null);

  // Register data field value
  // ------------------------------

  useOnce(() => {
    if (id) {
      registerDataFieldValue(id, resolvedValue);
    }
  }, [id]);

  // Visibility
  // ------------------------------

  const { isVisible, isNew } = useVisibleIf(visibleIf);
  const { isDisabled } = useDisabledIf(disabledIf);

  // Register visibility with parent Form (if within one)
  useRegisterInputVisibility(id, isVisible);

  // Debounced state
  // ------------------------------

  const {
    state: value,
    setState: setValue,
    setImmediateState: setImmediateValue,
  } = useDebouncedState<DataFieldValue>(
    resolvedValue,
    {
      debounced: async (value) => {
        if (successTimeOutRef.current) {
          clearTimeout(successTimeOutRef.current);
        }

        if (validationRef.current && isDirtyRef.current) {
          // console.log("[useDebouncedState] Validation", validationRef.current);

          if (validationRef.current.success) {
            isDirtyRef.current = false;

            if (endpoint) {
              // Resolve `{id}` placeholders in linkedFields using the current
              // parent context ID. This is needed for blueprint fields inside
              // modelChildren where `{id}` can't be substituted statically.
              const resolvedLinkedFields = parentID
                ? linkedFields?.map((f) => f.replace(/\{id\}/g, parentID))
                : linkedFields;

              try {
                const response = await updateDataFieldValue(
                  endpoint,
                  value,
                  resolvedLinkedFields,
                );

                if (response.error) {
                  isDirtyRef.current = true;
                  setError(response.error);
                } else if (response.success) {
                  setSuccess(true);
                  setIsHighlighted(false);
                }
              } catch (error) {
                isDirtyRef.current = true;
                throw error;
              }
            }
          }
        }
      },
      immediate: useCallback(
        (value) => {
          isDirtyRef.current = true;

          if (id) {
            if (FormInputPopupTypeOptions.includes(type)) {
              const label = props.selectOptions?.find(
                (option: { label: string; value: DataFieldValue }) =>
                  option.value === value,
              )?.label;

              registerDataFieldValue(id, value, label);
            } else {
              registerDataFieldValue(id, value);
            }
          }

          setError(null);
          setSuccess(false);
          if (successTimeOutRef.current) {
            clearTimeout(successTimeOutRef.current);
          }
        },
        [id, registerDataFieldValue, props.selectOptions, type],
      ),
    },
    1000,
  );

  // Change handler
  // ------------------------------

  const handleChange: FormInputProps["onChange"] = (value, validation) => {
    validationRef.current = validation;
    setValue(value);
  };

  // Successfull update
  // ------------------------------

  const successTimeOutRef = useRef<Timeout | null>(null);

  useEffect(() => {
    if (hasSuccess) {
      successTimeOutRef.current = setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } else if (successTimeOutRef.current) {
      clearTimeout(successTimeOutRef.current);
    }

    return () => {
      if (successTimeOutRef.current) {
        clearTimeout(successTimeOutRef.current);
      }
    };
  }, [hasSuccess]);

  // Highlighting
  // ------------------------------

  const highlightTimeoutRef = useRef<Timeout | null>(null);
  const [isHighlighted, setIsHighlighted] = useState(false);

  useEffect(() => {
    if (props.highlight) {
      highlightTimeoutRef.current = setTimeout(() => {
        setIsHighlighted(true);
      }, 1000);
    }
  }, [props.highlight]);

  useEffect(() => {
    const unsubscribeHighlight = pubSub.subscribe(
      "inputHighlight",
      ({ ids }) => {
        if (id && ids.includes(id)) {
          setIsHighlighted(true);
          if (highlightTimeoutRef.current) {
            clearTimeout(highlightTimeoutRef.current);
          }
          highlightTimeoutRef.current = setTimeout(() => {
            setIsHighlighted(false);
          }, 5000);
        }
      },
    );

    const unsubscribeUpdate = pubSub.subscribe("inputUpdate", (payload) => {
      if (!payload) return;

      const updates = isArray(payload) ? payload : [payload];

      for (const update of updates) {
        if (!update) continue;
        const { id: updateID, value, highlight } = update;
        if (id === updateID) {
          log("purple", "FormInputTemplate", "pubSub(inputUpdate)", value);
          setImmediateValue?.(value);

          // Register the value to shared context so disabledIf/visibleIf rules work
          if (id) {
            registerDataFieldValue(id, value);
          }

          if (highlight && id) {
            pubSub.publish("inputHighlight", { ids: [id] });
          }
          break;
        }
      }
    });

    return () => {
      unsubscribeHighlight();
      unsubscribeUpdate();
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, [id]);

  return isVisible ? (
    <FormInput
      ref={ref}
      {...props}
      id={id}
      type={type}
      disabled={disabled || isDisabled}
      // success={hasSuccess}
      error={error ?? undefined}
      highlight={isNew || isHighlighted}
      value={value === null ? undefined : value}
      onChange={handleChange}
      basis={width}
      constraints={validation}
    />
  ) : null;
};
